// Subscribes to CombatEngine events and schedules VFX.
// In real-time mode the engine never waits for animations — all VFX are flourishes.

import { TIMING } from "../config/constants";
import type { EventBus } from "../engine/event/EventBus";
import type { CombatEvents } from "../combat/events";
import type { SlotLayout } from "../engine/SlotLayout";
import { AnimationQueue } from "../engine/animation/AnimationQueue";
import type { Unit } from "../units/Unit";
import { UnitVisual } from "./UnitVisual";
import { HealthBar } from "./HealthBar";
import { CooldownBar } from "./CooldownBar";
import { AttackBar } from "./AttackBar";
import { AttackAnim } from "./vfx/AttackAnim";
import { AoERingVFX } from "./vfx/AoERingVFX";
import { BeamVFX } from "./vfx/BeamVFX";
import { DamagePopup } from "./vfx/DamagePopup";
import { HitFlashVFX } from "./vfx/HitFlashVFX";
import { DeathAnim } from "./vfx/DeathAnim";
import { OrbVFX, type OrbHit } from "./vfx/OrbVFX";
import { KnockupVFX } from "./vfx/KnockupVFX";
import { ProjectileVFX } from "./vfx/ProjectileVFX";
import { SpinVFX, SPIN_COUNT } from "./vfx/SpinVFX";
import { TentacleVFX } from "./vfx/TentacleVFX";
import { ShieldVFX } from "./vfx/ShieldVFX";
import { HealFlashVFX } from "./vfx/HealFlashVFX";
import { AlphaStrikeVFX } from "./vfx/SlashVFX";
import { JarvanDashVFX } from "./vfx/JarvanDashVFX";
import { CaitlynHeadshotVFX } from "./vfx/CaitlynHeadshotVFX";
import { HookVFX } from "./vfx/HookVFX";
import { KatarinaHighlightVFX, KatarinaDaggerThrowVFX, KatarinaIconSpinVFX } from "./vfx/KatarinaVFX";
import { KAT_WINDUP_MS, KAT_STAGGER_MS, KAT_TRAVEL_MS } from "../units/abilities/KatarinaDeathLotus";
import { RequiemWindupVFX, RequiemBeamVFX } from "./vfx/RequiemVFX";
import { OlafRagnarokVFX } from "./vfx/OlafRagnarokVFX";
import { AsheArrowVFX } from "./vfx/AsheArrowVFX";
import { ChoGathRuptureVFX } from "./vfx/ChoGathRuptureVFX";
import { JaxCounterStrikeVFX } from "./vfx/JaxCounterStrikeVFX";
import { JaxSlamVFX } from "./vfx/JaxSlamVFX";
import { LightningZapVFX } from "./vfx/LightningZapVFX";
import { MundoUltVFX } from "./vfx/MundoUltVFX";
import { SorakaWishVFX } from "./vfx/SorakaWishVFX";
import { YuumiBondVFX } from "./vfx/YuumiBondVFX";
import { TaricDazzleVFX } from "./vfx/TaricDazzleVFX";
import { TibbersSpawnVFX } from "./vfx/TibbersSpawnVFX";
import { LuxBeamVFX } from "./vfx/LuxBeamVFX";
import { YorickGhoulSpawnVFX } from "./vfx/YorickGhoulSpawnVFX";
import { launchRocketSalvo } from "./vfx/KaisaVFX";
import { KAISA_WINDUP_MS, KAISA_TARGET_STAGGER_MS } from "../units/abilities/KaisaUlt";
import { DravenAxeVFX } from "./vfx/DravenAxeVFX";
import { JinxRocketVFX } from "./vfx/JinxRocketVFX";
import { PantheonSpearVFX } from "./vfx/PantheonSpearVFX";
import { ZoeBubbleVFX } from "./vfx/ZoeBubbleVFX";
import { MalphiteUltDashVFX } from "./vfx/MalphiteUltDashVFX";
import { NidaleeSpearVFX, NidaleePounceVFX } from "./vfx/NidaleeVFX";
import { NeekoBounceVFX } from "./vfx/NeekoBounceVFX";
import { SivirRicochetVFX } from "./vfx/SivirRicochetVFX";
import { DariusAxeSwingVFX } from "./vfx/DariusAxeSwingVFX";
import { LissandraFrostVFX } from "./vfx/LissandraFrostVFX";
import { SylasChainVFX } from "./vfx/SylasChainVFX";
import { XerathBoltVFX } from "./vfx/XerathBoltVFX";
import { AurelionStarVFX } from "./vfx/AurelionStarVFX";
import { NasusAxeVFX } from "./vfx/NasusAxeVFX";
import { ViDashVFX } from "./vfx/ViDashVFX";
import { AzirDashVFX } from "./vfx/AzirDashVFX";
import { AurelionMeteorVFX } from "./vfx/AurelionMeteorVFX";
import { RenektonFuryVFX } from "./vfx/RenektonFuryVFX";
import type { HexCoord } from "../engine/hex/HexCoord";
import type { StatusEffect } from "../units/abilities/Ability";

type SpinPopup = { x: number; y: number; amount: number; color: string };
type AlphaHit = { x: number; y: number; amount: number; color: string };
type NeekoHit = { x: number; y: number; amount: number; color: string; visual: UnitVisual; unitId: string; died: boolean };
type JaxSlamHit = { x: number; y: number; amount: number; color: string; visual: UnitVisual };

export class CombatAnimator {
  private queue = new AnimationQueue();
  private visuals = new Map<string, UnitVisual>();
  private healthBars = new Map<string, HealthBar>();
  private cooldownBars = new Map<string, CooldownBar>();
  private attackBars = new Map<string, AttackBar>();
  private unsubscribers: (() => void)[] = [];
  private pendingSpinPopups: SpinPopup[] = [];
  private spinPopupCursor = 0;
  private pendingOrbPopups: OrbHit[] = [];
  private pendingAlphaHits: AlphaHit[] = [];
  private dashedUnits = new Set<string>(); // units whose move is handled by cast VFX
  private pendingJarvanHits: AlphaHit[] = [];
  private pendingEzrealHit: AlphaHit | null = null;
  private pendingTentacleHit: AlphaHit | null = null;
  private pendingCaitlynHeadshotHit: AlphaHit | null = null;
  private activeSpinVfx = new Map<string, SpinVFX>(); // unit id → active SpinVFX
  private blockingVfxCount = 0; // non-spin VFX that also block the turn timer
  private pendingHookHit: AlphaHit | null = null;
  private pendingKatarinaHits: SpinPopup[] = [];
  private pendingRequiemHits = new Map<string, AlphaHit>();
  private pendingLuxHits: AlphaHit[] = [];
  private mundoLightningCasterPos: { x: number; y: number } | null = null;
  private pendingKaisaHits: NeekoHit[] = [];
  private pendingKaisaDeaths = new Set<string>();
  private pendingSivirHits: AlphaHit[] = [];
  private pendingDravenHits: AlphaHit[] = [];
  private pendingJinxHits: AlphaHit[] = [];
  private pendingNidaleeSpearHit: AlphaHit | null = null;
  private pendingNidaleeBiteHit: AlphaHit | null = null;
  private pendingNeekoBounceHits: NeekoHit[] = [];
  private pendingNeekoDeaths = new Set<string>();
  private pendingViDashHit: AlphaHit | null = null;
  private pendingAzirDashHits: AlphaHit[] = [];
  private pendingNasusAxeHit: AlphaHit | null = null;
  private pendingJaxSlamHits: JaxSlamHit[] = [];
  private movementPauseMs = 0; // blocks lerpUnitPositions while VFX are still playing
  // setTimeout IDs for VFX scheduled outside the animation queue (Jinx rocket stagger,
  // Lux beam hit popups, Draven axe stagger). Tracked so the scene can wait for them
  // to fire before showing the end-of-combat banner.
  private pendingTimers = new Set<number>();

  constructor(
    private bus: EventBus<CombatEvents>,
    private layout: SlotLayout,
    private onSpinPulse?: (pulseNum: number, totalPulses: number) => void,
  ) {}

  setLayout(layout: SlotLayout): void {
    this.layout = layout;
  }

  registerUnit(unit: Unit): UnitVisual {
    const p = this.layout.hexToPixel(unit.pos);
    const visual = new UnitVisual(unit, p.x, p.y);
    this.visuals.set(unit.id, visual);
    this.healthBars.set(unit.id, new HealthBar(unit));
    this.cooldownBars.set(unit.id, new CooldownBar(unit));
    this.attackBars.set(unit.id, new AttackBar(unit));
    return visual;
  }

  getVisual(unit: Unit): UnitVisual | undefined {
    return this.visuals.get(unit.id);
  }

  getHealthBar(unit: Unit): HealthBar | undefined {
    return this.healthBars.get(unit.id);
  }

  getCooldownBar(unit: Unit): CooldownBar | undefined {
    return this.cooldownBars.get(unit.id);
  }

  getAttackBar(unit: Unit): AttackBar | undefined {
    return this.attackBars.get(unit.id);
  }

  get unitVisuals(): IterableIterator<UnitVisual> {
    return this.visuals.values();
  }

  isBlockingCast(): boolean {
    return this.activeSpinVfx.size > 0 || this.blockingVfxCount > 0;
  }

  // True when nothing visual is in flight: queue is drained, no scheduled VFX timers
  // pending, and no spin/blocking VFX active. The scene uses this to defer the end
  // banner until rockets have landed and damage popups have faded.
  isVfxIdle(): boolean {
    return (
      this.queue.isFullyIdle() &&
      this.pendingTimers.size === 0 &&
      this.activeSpinVfx.size === 0 &&
      this.blockingVfxCount <= 0
    );
  }

  debugState(): object {
    return {
      blockingVfxCount: this.blockingVfxCount,
      activeSpinVfx: this.activeSpinVfx.size,
      pendingTimers: this.pendingTimers.size,
      queueFullyIdle: this.queue.isFullyIdle(),
    };
  }

  private scheduleVfx(fn: () => void, delayMs: number): void {
    const id = window.setTimeout(() => {
      this.pendingTimers.delete(id);
      fn();
    }, delayMs);
    this.pendingTimers.add(id);
  }

  attach(): void {
    this.unsubscribers.push(
      this.bus.on("unit.attacked", (e) => this.onAttack(e.attacker, e.target, e.isEmpowered)),
      this.bus.on("unit.casted", (e) =>
        this.onCast(e.caster, e.target, e.abilityId, e.shape),
      ),
      this.bus.on("damage.dealt", (e) =>
        this.onDamage(e.target, e.amount, e.type, e.suppressPopup, e.orbPass, e.alphaSlice, e.jarvanPass, e.ezrealPass, e.tentacleHit, e.caitlynHeadshot, e.hookPass, e.katarinaSlice, e.requiemBeam, e.luxBeam, e.mundoLightning, e.chogathRupture, e.kaisaRocket, e.sivirBounce, e.dravenAxe, e.jinxRocket, e.nidaleeSpear, e.nidaleeBite, e.neekoBounce, e.viDash, e.azirDash, e.nasusAxe, e.jaxSlam),
      ),
      this.bus.on("unit.statusApplied", (e) =>
        this.onStatusApplied(e.unit, e.effect),
      ),
      this.bus.on("unit.died", (e) => this.onDied(e.unit)),
      this.bus.on("unit.healed", (e) => this.onHealed(e.unit, e.amount)),
      this.bus.on("unit.spawned", (e) => this.onSpawned(e.unit)),
      this.bus.on("unit.dodged", (e) => this.onDodged(e.unit)),
      this.bus.on("jax.slammed", (e) => this.onJaxSlam(e.unit)),
      this.bus.on("vayne.silverBolts", (e) =>
        this.onVayneSilverBolts(e.target, e.addedStacks, e.procced),
      ),
    );
  }

  private onVayneSilverBolts(target: Unit, addedStacks: number, procced: boolean): void {
    const visual = this.visuals.get(target.id);
    if (!visual) return;
    const RING_DELAY_MS = 110;
    for (let i = 0; i < addedStacks; i++) {
      const delay = i * RING_DELAY_MS;
      if (delay === 0) {
        visual.vayneVisualStacks = Math.min(3, visual.vayneVisualStacks + 1);
      } else {
        this.scheduleVfx(() => {
          visual.vayneVisualStacks = Math.min(3, visual.vayneVisualStacks + 1);
        }, delay);
      }
    }
    if (procced) {
      const popDelay = addedStacks * RING_DELAY_MS;
      this.scheduleVfx(() => {
        // Quick expanding flash + reset rings.
        this.queue.pushFlourish(new AoERingVFX(
          { x: visual.x, y: visual.y },
          this.layout.size * 1.4,
          "#f0f0f0",
        ));
        this.queue.pushFlourish(new HitFlashVFX(visual));
        visual.vayneVisualStacks = 0;
      }, popDelay);
    }
  }

  detach(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    for (const id of this.pendingTimers) clearTimeout(id);
    this.pendingTimers.clear();
  }

  update(dt: number): void {
    this.queue.update(dt);
    if (this.movementPauseMs > 0) this.movementPauseMs = Math.max(0, this.movementPauseMs - dt);
    for (const bar of this.healthBars.values()) bar.update(dt);
    for (const bar of this.cooldownBars.values()) bar.update(dt);
    for (const bar of this.attackBars.values()) bar.update(dt);
    this.lerpUnitPositions(dt);
  }

  drawEffects(ctx: CanvasRenderingContext2D): void {
    this.queue.draw(ctx);
  }

  // Returns the current pixel position of a unit's visual (falls back to layout if no visual).
  private getUnitPos(unit: Unit): { x: number; y: number } {
    const v = this.visuals.get(unit.id);
    return v ? { x: v.x, y: v.y } : this.layout.hexToPixel(unit.pos);
  }

  // Smoothly move each unit visual toward its rank-based position among living teammates.
  // When a frontline unit dies, survivors shift forward — creating the close-up animation.
  private lerpUnitPositions(dt: number): void {
    if (this.movementPauseMs > 0) return;
    const speed = 300; // pixels per second
    const dtSec = dt / 1000;

    // Build sorted living-unit lists per team (rank 0 = frontline slot)
    const living: Record<"player" | "enemy", UnitVisual[]> = { player: [], enemy: [] };
    for (const visual of this.visuals.values()) {
      if (visual.unit.currentHp > 0) {
        living[visual.unit.team as "player" | "enemy"].push(visual);
      }
    }
    living.player.sort((a, b) => a.unit.pos.q - b.unit.pos.q);
    living.enemy.sort((a, b) => a.unit.pos.q - b.unit.pos.q);

    for (const visual of this.visuals.values()) {
      if (visual.dead || this.dashedUnits.has(visual.unit.id)) continue;
      const team = visual.unit.team as "player" | "enemy";
      const teamLiving = living[team];
      const rank = teamLiving.indexOf(visual);
      const total = teamLiving.length;
      const target = rank >= 0
        ? this.layout.rankToPixel(rank, total, team)
        : this.layout.hexToPixel(visual.unit.pos);
      const dx = target.x - visual.x;
      const dy = target.y - visual.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) {
        visual.x = target.x;
        visual.y = target.y;
      } else {
        const move = Math.min(dist, speed * dtSec);
        visual.x += (dx / dist) * move;
        visual.y += (dy / dist) * move;
      }
    }
  }

  // ---- Event handlers ----

  private onAttack(attacker: Unit, target: Unit, isEmpowered?: boolean): void {
    this.movementPauseMs = 600;
    const visual = this.visuals.get(attacker.id);
    if (!visual) return;
    const from = { x: visual.x, y: visual.y };
    const to = this.getUnitPos(target);
    if (attacker.def.abilityId === "pantheonShield") {
      this.queue.pushFlourish(new PantheonSpearVFX(from, to));
      return;
    }
    this.queue.pushFlourish(new AttackAnim(visual, from, to));
  }

  private onCast(
    caster: Unit,
    target: Unit,
    abilityId: string,
    shape: HexCoord[],
  ): void {
    this.movementPauseMs = 600;
    const casterPos = this.getUnitPos(caster);
    const targetPos = this.getUnitPos(target);

    switch (abilityId) {
      case "ahriOrb": {
        const last = shape[shape.length - 1] ?? target.pos;
        const endPixel = this.layout.hexToPixel(last);
        this.pendingOrbPopups = [];
        const hits = this.pendingOrbPopups;
        this.queue.pushFlourish(
          new OrbVFX(casterPos, endPixel, "#00b4ff", hits, (hit) => {
            this.queue.pushFlourish(new DamagePopup(hit.x, hit.y - 18, hit.amount, hit.color));
          }),
        );
        break;
      }
      case "alistarPulverize": {
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size * 1.8, "#c0a060"));
        for (const visual of this.visuals.values()) {
          if (visual.unit.team === caster.team || visual.unit.currentHp <= 0) continue;
          const inShape = shape.some(h => h.q === visual.unit.pos.q && h.r === visual.unit.pos.r);
          if (inShape) this.queue.pushFlourish(new KnockupVFX(visual, this.layout.size));
        }
        break;
      }
      case "ezrealMysticShot": {
        const impactHex = shape[shape.length - 1] ?? target.pos;
        const impactPos = this.layout.hexToPixel(impactHex);
        this.pendingEzrealHit = null;
        const getHit = () => this.pendingEzrealHit;
        this.queue.pushFlourish(new ProjectileVFX(casterPos, impactPos, "#4af0ff", 5, () => {
          const h = getHit();
          if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
        }));
        break;
      }
      case "jinxFishbones": {
        const ROCKET_STAGGER = 320;
        const launchFrom = { x: casterPos.x, y: casterPos.y };
        this.pendingJinxHits = [];
        const jinxHits = this.pendingJinxHits;
        // Hits resolve synchronously before any setTimeout fires, so jinxHits is
        // fully populated by the time we read [rocketIndex]. A missing entry means
        // the hit found no living target after a kill — just skip that rocket.
        for (let i = 0; i < 3; i++) {
          const rocketIndex = i;
          this.scheduleVfx(() => {
            const h = jinxHits[rocketIndex];
            if (!h) return;
            const launchTo = { x: h.x, y: h.y };
            this.queue.pushFlourish(new JinxRocketVFX(launchFrom, launchTo, () => {
              this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            }));
          }, i * ROCKET_STAGGER);
        }
        break;
      }
      case "sivirRicochet": {
        if (shape.length === 0) break;
        const sivirEnemyTeam = caster.team === "player" ? "enemy" : "player";
        const sivirTargetPxs = shape.map(h => {
          const v = [...this.visuals.values()].find(
            vis => vis.unit.pos.q === h.q && vis.unit.pos.r === h.r && vis.unit.team === sivirEnemyTeam
          );
          return v ? { x: v.x, y: v.y } : this.layout.hexToPixel(h);
        });
        // positions: [casterPos, primary, bounce1, bounce2, ...]
        const sivirPositions = [casterPos, ...sivirTargetPxs];
        this.pendingSivirHits = [];
        const sivirHits = this.pendingSivirHits;
        let sivirHitIdx = 0;
        this.queue.pushFlourish(new SivirRicochetVFX(sivirPositions, () => {
          const h = sivirHits[sivirHitIdx++];
          if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
        }));
        break;
      }
      case "garenJudgment": {
        this.pendingSpinPopups = [];
        this.spinPopupCursor = 0;
        let spinPulseCount = 0;
        const garenVisual = this.visuals.get(caster.id);
        const enemyR = caster.team === "player" ? 1 : 0;
        const slot0px = this.layout.hexToPixel({ q: 0, r: enemyR });
        const slot1px = this.layout.hexToPixel({ q: 1, r: enemyR });
        const spinCenter = { x: (slot0px.x + slot1px.x) / 2, y: (slot0px.y + slot1px.y) / 2 };
        const spinVFX = new SpinVFX(
          casterPos,
          this.layout.size * 3.2,
          "#c8a040",
          (spinIndex) => {
            if (spinIndex % 2 !== 1) return;
            spinPulseCount++;
            this.onSpinPulse?.(spinPulseCount, 3);
            // pendingSpinPopups fills as damage.dealt events fire from onSpinPulse.
            const newHits = this.pendingSpinPopups.slice(this.spinPopupCursor);
            for (const p of newHits) {
              this.queue.pushFlourish(new DamagePopup(p.x, p.y - 18, p.amount, p.color));
            }
            this.spinPopupCursor = this.pendingSpinPopups.length;
          },
          garenVisual,
          spinCenter,
        );
        this.activeSpinVfx.set(caster.id, spinVFX);
        if (garenVisual) this.dashedUnits.add(caster.id);
        spinVFX.onEnd = () => {
          this.activeSpinVfx.delete(caster.id);
          if (garenVisual) {
            garenVisual.x = casterPos.x;
            garenVisual.y = casterPos.y;
            this.dashedUnits.delete(caster.id);
          }
        };
        this.queue.pushFlourish(spinVFX);
        break;
      }
      case "illaoiTentacle": {
        this.pendingTentacleHit = null;
        // shape.length encodes the number of tentacles for this cast.
        const tentacleCount = shape.length || 1;
        const baseRadius = this.layout.size * 1.5;
        // Half-spread (radians) from straight-down, widening with more tentacles.
        const halfSpreadMap: Record<number, number> = { 1: 0, 2: 0.3, 3: 0.42, 4: 0.5 };
        const halfSpread = (halfSpreadMap[tentacleCount] ?? 0.5) * Math.PI;

        for (let i = 0; i < tentacleCount; i++) {
          const frac = tentacleCount === 1 ? 0.5 : i / (tentacleCount - 1);
          // Angles spread symmetrically around π/2 (straight below in screen coords).
          const angle = Math.PI / 2 - halfSpread + frac * 2 * halfSpread;
          const basePos = {
            x: targetPos.x + Math.cos(angle) * baseRadius,
            y: targetPos.y + Math.sin(angle) * baseRadius,
          };
          const vfx = new TentacleVFX(targetPos, basePos, this.layout.size * 0.8);
          if (i === 0) {
            vfx.onImpact = () => {
              const h = this.pendingTentacleHit;
              if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            };
          }
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "jarvanCataclysm": {
        const landingHex = shape[shape.length - 1] ?? shape[0];
        const landingPx = this.layout.hexToPixel(landingHex);
        const hitPositions = shape.slice(1, -1).map((h) => this.layout.hexToPixel(h));
        const casterVisual = this.visuals.get(caster.id);
        if (casterVisual) {
          // caster.pos is already at landing hex (teleport fires before this event),
          // so use the visual's current pixel position as the dash origin.
          const startPx = { x: casterVisual.x, y: casterVisual.y };
          this.pendingJarvanHits = [];
          const jarvanHits = this.pendingJarvanHits;
          const vfx = new JarvanDashVFX(
            casterVisual, startPx, landingPx, this.layout.size,
            hitPositions,
            (index) => {
              const hit = jarvanHits[index];
              if (hit) {
                this.queue.pushFlourish(new DamagePopup(hit.x, hit.y - 18, hit.amount, hit.color));
              }
            },
          );
          this.dashedUnits.add(caster.id);
          vfx.onEnd = () => {
            casterVisual.x = landingPx.x;
            casterVisual.y = landingPx.y;
            this.dashedUnits.delete(caster.id);
          };
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "khazixLeap": {
        // Kha'Zix is now a melee strike — lunge toward target with a purple ring burst.
        const casterVisual = this.visuals.get(caster.id);
        if (casterVisual) {
          const from = { x: casterVisual.x, y: casterVisual.y };
          this.queue.pushFlourish(new AttackAnim(casterVisual, from, targetPos));
          this.queue.pushFlourish(new AoERingVFX(targetPos, this.layout.size * 0.9, "#8844cc"));
        }
        break;
      }
      case "blitzcrankHook": {
        // shape = hexLine caster→target; last hex is the target's original position
        const hookTargetHex = shape[shape.length - 1] ?? target.pos;
        const hookOriginPx  = this.layout.hexToPixel(hookTargetHex);
        // target.pos is already the pull destination (teleport fires before onCast)
        const pullDestPx    = this.layout.hexToPixel(target.pos);
        const hookTargetVisual = this.visuals.get(target.id);
        this.pendingHookHit = null;

        if (hookTargetVisual) {
          // Freeze visual at the original position — HookVFX drives x/y directly
          hookTargetVisual.x = hookOriginPx.x;
          hookTargetVisual.y = hookOriginPx.y;
          this.dashedUnits.add(target.id);

          const vfx = new HookVFX(
            casterPos,
            hookOriginPx,
            pullDestPx,
            () => {
              const h = this.pendingHookHit;
              if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            },
            hookTargetVisual,
          );
          vfx.onYankComplete = () => {
            hookTargetVisual.x = pullDestPx.x;
            hookTargetVisual.y = pullDestPx.y;
            this.dashedUnits.delete(target.id);
          };
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "katarinaDeathLotus": {
        // shape[0] = caster pos, shape[1..] = all enemy hexes
        const targetPxs = shape.slice(1).map((h) => this.layout.hexToPixel(h));
        if (targetPxs.length === 0) break;
        const katCasterVisual = this.visuals.get(caster.id);
        this.pendingKatarinaHits = [];

        // Highlight all enemies
        this.queue.pushFlourish(new KatarinaHighlightVFX(targetPxs, this.layout.size));

        // Icon spin on Katarina covering the full dagger-throw phase
        if (katCasterVisual) {
          const spinDuration = (targetPxs.length - 1) * KAT_STAGGER_MS + KAT_TRAVEL_MS + 320;
          this.queue.pushFlourish(new KatarinaIconSpinVFX(katCasterVisual, KAT_WINDUP_MS, spinDuration));
        }

        // One dagger per enemy, thrown from caster position
        for (let i = 0; i < targetPxs.length; i++) {
          const tgtPx = targetPxs[i];
          this.queue.pushFlourish(new KatarinaDaggerThrowVFX(
            casterPos, tgtPx,
            KAT_WINDUP_MS + i * KAT_STAGGER_MS,
            () => {
              const h = this.pendingKatarinaHits.shift();
              if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            },
          ));
        }
        break;
      }
      case "karthusRequiem": {
        // Wind-up aura on Karthus
        this.queue.pushFlourish(new RequiemWindupVFX(casterPos, this.layout.size));

        // Match each shape hex to the unit visual that was standing there at cast time
        const enemyVisualsByHex = shape.map(hex =>
          [...this.visuals.values()].find(
            v => v.unit.pos.q === hex.q && v.unit.pos.r === hex.r && v.unit.team !== caster.team
          ) ?? null
        );
        const fallbackPxByHex = shape.map(hex => this.layout.hexToPixel(hex));

        this.pendingRequiemHits.clear();
        const WINDUP_DELAY = 1000;
        const STAGGER = 120;
        shape.forEach((_hex, i) => {
          const visual = enemyVisualsByHex[i];
          const fallback = fallbackPxByHex[i];
          // Getter reads the unit visual's live pixel position every frame
          const getTargetPos = visual
            ? () => ({ x: visual.x, y: visual.y })
            : () => fallback;

          const unitId = visual?.unit.id ?? null;
          const beamDelay = WINDUP_DELAY + i * STAGGER;
          const beam = new RequiemBeamVFX(
            getTargetPos,
            0,
            this.layout.size,
            beamDelay,
            () => {
              // Look up the hit by unit id first, then fall back to closest
              let hit = unitId ? this.pendingRequiemHits.get(unitId) : null;
              if (!hit) {
                const pos = getTargetPos();
                let minDist = Infinity;
                for (const h of this.pendingRequiemHits.values()) {
                  const d = Math.hypot(h.x - pos.x, h.y - pos.y);
                  if (d < minDist) { minDist = d; hit = h; }
                }
              }
              if (hit) {
                this.queue.pushFlourish(new DamagePopup(hit.x, hit.y - 18, hit.amount, hit.color));
              }
            },
          );
          this.queue.pushFlourish(beam);
        });
        break;
      }
      case "olafRagnarok": {
        this.queue.pushFlourish(new OlafRagnarokVFX(casterPos, this.layout.size * 1.8));
        this.queue.pushFlourish(new DamagePopup(casterPos.x, casterPos.y - 28, 0, "#ff3300", "RAGNARÖK!"));
        break;
      }
      case "chogathRupture": {
        // targetPos is the cluster center (first hit unit's position)
        this.queue.pushFlourish(new ChoGathRuptureVFX(targetPos, this.layout.size * 2.2));
        break;
      }
      case "asheVolley": {
        // Single ice arrow fires only at the stunned (primary) target
        this.queue.pushFlourish(new AsheArrowVFX(casterPos, targetPos));
        break;
      }
      case "jaxCounterStrike": {
        const jaxVisual = this.visuals.get(caster.id);
        const getJaxPos = jaxVisual
          ? () => ({ x: jaxVisual.x, y: jaxVisual.y })
          : () => casterPos;
        const isStanceActive = () => caster.jaxCounterStrikeTurnsRemaining > 0;
        this.queue.pushFlourish(new JaxCounterStrikeVFX(getJaxPos, this.layout.size * 1.5, isStanceActive));
        this.queue.pushFlourish(new DamagePopup(casterPos.x, casterPos.y - 28, 0, "#a855f7", "COUNTER STRIKE!"));
        break;
      }
      case "mundoUlt": {
        this.mundoLightningCasterPos = { x: casterPos.x, y: casterPos.y };
        this.queue.pushFlourish(new MundoUltVFX(casterPos, this.layout.size * 1.6));
        this.queue.pushFlourish(new DamagePopup(casterPos.x, casterPos.y - 28, 0, "#22c55e", "DOSAGE!"));
        break;
      }
      case "sorakaWish": {
        this.queue.pushFlourish(new SorakaWishVFX(casterPos, targetPos, this.layout.size));
        break;
      }
      case "taricDazzle": {
        const hexCenters = shape.map(h => this.layout.hexToPixel(h));
        const hitPts = [...this.visuals.values()]
          .filter(v => v.unit.team !== caster.team && v.unit.currentHp > 0
            && shape.some(h => h.q === v.unit.pos.q && h.r === v.unit.pos.r))
          .map(v => this.layout.hexToPixel(v.unit.pos));
        this.queue.pushFlourish(new TaricDazzleVFX(hexCenters, hitPts, this.layout.size));
        break;
      }
      case "annieTibbers": {
        // shape[0] is the spawn hex chosen by the ability
        const spawnHex = shape[0] ?? target.pos;
        const spawnPx = this.layout.hexToPixel(spawnHex);
        this.queue.pushFlourish(new TibbersSpawnVFX(spawnPx, this.layout.size));
        this.queue.pushFlourish(new DamagePopup(spawnPx.x, spawnPx.y - 32, 0, "#ff4400", "TIBBERS!"));
        break;
      }
      case "yorickSummonGhoul": {
        const ghoulHex = shape[0] ?? target.pos;
        const ghoulPx = this.layout.hexToPixel(ghoulHex);
        this.queue.pushFlourish(new YorickGhoulSpawnVFX(ghoulPx, this.layout.size));
        break;
      }
      case "luxFinalSpark": {
        const lastHex = shape[shape.length - 1] ?? target.pos;
        const endPixel = this.layout.hexToPixel(lastHex);
        this.pendingLuxHits = [];
        const luxHits = this.pendingLuxHits;
        // Windup charge pulse on Lux
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size * 0.9, "#c084fc"));
        // Laser beam across the full map
        const beamVfx = new LuxBeamVFX(casterPos, endPixel);
        // Release damage popups mid-beam (staggered by position along the line)
        const SPARK_DELAY = 420; // ms after cast before first hit popup
        const HIT_STAGGER = 80;
        for (let i = 0; i < shape.length; i++) {
          const hexPx = this.layout.hexToPixel(shape[i]);
          const hitIndex = i;
          const delay = SPARK_DELAY + hitIndex * HIT_STAGGER;
          this.scheduleVfx(() => {
            const h = luxHits[hitIndex];
            if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
          }, delay);
        }
        this.queue.pushFlourish(beamVfx);
        break;
      }
      case "leonaShield": {
        this.queue.pushFlourish(new ShieldVFX(casterPos, this.layout.size * 1.4));
        break;
      }
      case "kaisaUlt": {
        // shape = target hexes only (no dash).
        const kaisaTargetHexPxs = shape.map((h) => this.layout.hexToPixel(h));
        const usedKaisaIds = new Set<string>();
        const kaisaTargetVisuals = kaisaTargetHexPxs.map((hPx) => {
          let best: UnitVisual | null = null;
          let bestDist = Infinity;
          for (const v of this.visuals.values()) {
            if (v.unit.team === caster.team || v.unit.currentHp <= 0) continue;
            if (usedKaisaIds.has(v.unit.id)) continue;
            const dx = v.x - hPx.x;
            const dy = v.y - hPx.y;
            const d = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; best = v; }
          }
          if (best) usedKaisaIds.add(best.unit.id);
          return best;
        });
        this.pendingKaisaHits = [];
        const kaisaHits = this.pendingKaisaHits;
        for (let ti = 0; ti < kaisaTargetVisuals.length; ti++) {
          const tv = kaisaTargetVisuals[ti];
          const fb = kaisaTargetHexPxs[ti];
          const getTo = tv ? () => ({ x: tv.x, y: tv.y }) : () => fb;
          launchRocketSalvo(
            this.queue,
            casterPos,
            getTo,
            KAISA_WINDUP_MS + ti * KAISA_TARGET_STAGGER_MS,
            4,
            () => {
              const h = kaisaHits.shift();
              if (!h) return;
              const healthBar = this.healthBars.get(h.unitId);
              if (healthBar) healthBar.unpin();
              this.queue.pushFlourish(new HitFlashVFX(h.visual));
              this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
              if (h.died) {
                this.pendingKaisaDeaths.delete(h.unitId);
                this.triggerDeathAnim(h.unitId);
              }
            },
          );
        }
        break;
      }
      case "masterYiAlphaStrike": {
        const angle = Math.atan2(targetPos.y - casterPos.y, targetPos.x - casterPos.x);
        const chainTargets = shape.slice(1).map((h) => this.layout.hexToPixel(h));
        this.pendingAlphaHits = [];
        const alphaHits = this.pendingAlphaHits;
        const casterVisual = this.visuals.get(caster.id);
        const vfx = new AlphaStrikeVFX(chainTargets, angle, this.layout.size * 1.15, (targetIndex) => {
          const start = targetIndex * 4;
          const group = alphaHits.slice(start, start + 4);
          const total = group.reduce((s, h) => s + h.amount, 0);
          if (group.length > 0) {
            this.queue.pushFlourish(new DamagePopup(group[0].x, group[0].y - 18, total, group[0].color));
          }
        });
        if (casterVisual) {
          vfx.onStart = () => { casterVisual.alpha = 0; };
          vfx.onEnd = () => { casterVisual.alpha = 1; };
        }
        this.queue.pushFlourish(vfx);
        break;
      }
      case "dravenSpinning": {
        this.pendingDravenHits = [];
        const dravenHits = this.pendingDravenHits;
        // passiveStacks was already incremented by computeHits, so its value now
        // equals the number of axes just thrown (capped at 4).
        const axeCount = Math.min(caster.passiveStacks, 4);
        const casterVisual = this.visuals.get(caster.id);
        const getCatchPos = () => casterVisual ? { x: casterVisual.x, y: casterVisual.y } : casterPos;

        const launchAxe = (delay: number) => {
          const axe = new DravenAxeVFX(
            { x: casterPos.x, y: casterPos.y },
            { x: targetPos.x, y: targetPos.y },
            getCatchPos,
            () => {
              const h = dravenHits.shift();
              if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            },
            () => {
              const pos = getCatchPos();
              this.queue.pushFlourish(new DamagePopup(pos.x, pos.y - 28, 0, "#ffcc44", "CATCH!"));
            },
          );
          if (delay === 0) {
            this.queue.pushFlourish(axe);
          } else {
            this.scheduleVfx(() => this.queue.pushFlourish(axe), delay);
          }
        };

        for (let i = 0; i < axeCount; i++) {
          launchAxe(i * 120);
        }
        break;
      }
      case "pantheonShield": {
        // Ability is a self-buff — show a golden shield aura on the caster
        this.queue.pushFlourish(new ShieldVFX(casterPos, this.layout.size * 1.1));
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size * 1.3, "#f0c040"));
        break;
      }
      case "viktorDeathRay": {
        // Laser beam from caster to target
        this.queue.pushFlourish(new BeamVFX(casterPos, targetPos, "#ccffcc", 4));
        break;
      }
      case "malphiteUltimate": {
        const malphiteHitVisuals = [...this.visuals.values()].filter(v =>
          v.unit.team !== caster.team && v.unit.currentHp > 0 &&
          shape.some(h => h.q === v.unit.pos.q && h.r === v.unit.pos.r)
        );
        const casterVisual = this.visuals.get(caster.id);
        if (casterVisual) {
          const startPx = { x: casterVisual.x, y: casterVisual.y };
          // Land at the center of all hit enemies
          const hitPixels = shape.map(h => this.layout.hexToPixel(h));
          const landingPx = hitPixels.length > 0
            ? { x: hitPixels.reduce((s, p) => s + p.x, 0) / hitPixels.length, y: hitPixels.reduce((s, p) => s + p.y, 0) / hitPixels.length }
            : targetPos;
          const vfx = new MalphiteUltDashVFX(
            casterVisual, startPx, landingPx, this.layout.size,
            () => {
              for (const v of malphiteHitVisuals) {
                this.queue.pushFlourish(new KnockupVFX(v, this.layout.size));
              }
            },
          );
          this.dashedUnits.add(caster.id);
          vfx.onEnd = () => {
            // Snap back instantly rather than slow-lerping across the map
            casterVisual.x = startPx.x;
            casterVisual.y = startPx.y;
            this.dashedUnits.delete(caster.id);
          };
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "lissandraIceTomb": {
        this.queue.pushFlourish(new LissandraFrostVFX(targetPos, this.layout.size * 1.5));
        break;
      }
      case "dariusApprehend": {
        this.queue.pushFlourish(new DariusAxeSwingVFX(casterPos, targetPos));
        break;
      }
      case "sylasChains": {
        this.queue.pushFlourish(new SylasChainVFX(casterPos, targetPos));
        break;
      }
      case "vladimirTransfusion": {
        // Blood drain beam
        this.queue.pushFlourish(new BeamVFX(casterPos, targetPos, "#dd1111", 6));
        break;
      }
      case "xerathArcaneBarrage": {
        if (shape.length === 0) break;  // ability did not trigger this turn
        const xerathTargets = shape.slice(0, 3).map(h => this.layout.hexToPixel(h));
        this.queue.pushFlourish(new XerathBoltVFX(casterPos, xerathTargets));
        break;
      }
      case "viVaultBreaker": {
        const viCasterVisual = this.visuals.get(caster.id);
        const viHitVisuals = [...this.visuals.values()].filter(v =>
          v.unit.team !== caster.team && v.unit.currentHp > 0 &&
          shape.some(h => h.q === v.unit.pos.q && h.r === v.unit.pos.r)
        );
        if (viCasterVisual) {
          const startPx = { x: viCasterVisual.x, y: viCasterVisual.y };
          this.pendingViDashHit = null;
          this.dashedUnits.add(caster.id);
          const vfx = new ViDashVFX(
            viCasterVisual, startPx, targetPos, this.layout.size,
            () => {
              for (const v of viHitVisuals) {
                this.queue.pushFlourish(new KnockupVFX(v, this.layout.size));
              }
              const h = this.pendingViDashHit;
              if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            },
          );
          vfx.onEnd = () => {
            viCasterVisual.x = startPx.x;
            viCasterVisual.y = startPx.y;
            this.dashedUnits.delete(caster.id);
          };
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "fioraRiposte": {
        // Parry flash at caster
        this.queue.pushFlourish(new ShieldVFX(casterPos, this.layout.size * 0.9));
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size, "#dddddd"));
        break;
      }
      case "tryndamereBloodlust": {
        // Spinning rage at caster position
        this.queue.pushFlourish(new SpinVFX(casterPos, this.layout.size * 1.4, "#dd2222"));
        break;
      }
      case "azirSoldier": {
        const azirVisual = this.visuals.get(caster.id);
        if (azirVisual) {
          const startPx = { x: azirVisual.x, y: azirVisual.y };
          // Last hex in shape is the farthest enemy; soldier spawns past it
          const lastHex = shape[shape.length - 1] ?? target.pos;
          const lastEnemyPx = this.layout.hexToPixel(lastHex);
          const dashAngle = Math.atan2(lastEnemyPx.y - startPx.y, lastEnemyPx.x - startPx.x);
          const soldierPx = {
            x: lastEnemyPx.x + Math.cos(dashAngle) * this.layout.size * 0.9,
            y: lastEnemyPx.y + Math.sin(dashAngle) * this.layout.size * 0.9,
          };
          this.pendingAzirDashHits = [];
          const azirHits = this.pendingAzirDashHits;
          let azirHitIdx = 0;
          this.dashedUnits.add(caster.id);
          const vfx = new AzirDashVFX(
            azirVisual, startPx, soldierPx, this.layout.size,
            () => {
              // Fire all damage popups when Azir reaches the soldier
              while (azirHitIdx < azirHits.length) {
                const h = azirHits[azirHitIdx++];
                this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
              }
              this.queue.pushFlourish(new DamagePopup(soldierPx.x, soldierPx.y - 36, 0, "#ffcc44", "SHIFTING SANDS!"));
            },
          );
          vfx.onEnd = () => {
            azirVisual.x = startPx.x;
            azirVisual.y = startPx.y;
            this.dashedUnits.delete(caster.id);
          };
          this.queue.pushFlourish(vfx);
        }
        break;
      }
      case "zoeSleepyTrouble": {
        this.queue.pushFlourish(new ZoeBubbleVFX(casterPos, targetPos));
        break;
      }
      case "aurelionStarfire": {
        if (shape.length === 0) break;
        // Compute enemy team center and ellipse bounds from shape
        const aurelionPixels = shape.map(h => this.layout.hexToPixel(h));
        const acx = aurelionPixels.reduce((s, p) => s + p.x, 0) / aurelionPixels.length;
        const acy = aurelionPixels.reduce((s, p) => s + p.y, 0) / aurelionPixels.length;
        const arx = Math.max(...aurelionPixels.map(p => Math.abs(p.x - acx))) + this.layout.size * 1.2;
        const ary = Math.max(...aurelionPixels.map(p => Math.abs(p.y - acy))) + this.layout.size * 0.8;
        const aurelionHitVisuals = [...this.visuals.values()].filter(v =>
          v.unit.team !== caster.team && v.unit.currentHp > 0
        );
        this.queue.pushFlourish(new AurelionMeteorVFX(
          { x: acx, y: acy }, arx, ary,
          () => {
            for (const v of aurelionHitVisuals) {
              this.queue.pushFlourish(new KnockupVFX(v, this.layout.size));
            }
          },
        ));
        break;
      }
      case "nasusSiphoning": {
        this.pendingNasusAxeHit = null;
        this.queue.pushFlourish(new NasusAxeVFX(
          targetPos,
          () => {
            const h = this.pendingNasusAxeHit;
            if (h) this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
          },
        ));
        break;
      }
      case "caitlynPeacemaker": {
        this.queue.pushFlourish(new CaitlynHeadshotVFX(casterPos, targetPos));
        break;
      }
      case "jayceMercuryHammer": {
        // Thunder hammer slam — electric ring + knockup
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size * 1.7, "#44bbff"));
        for (const visual of this.visuals.values()) {
          if (visual.unit.team === caster.team || visual.unit.currentHp <= 0) continue;
          const inShape = shape.some(h => h.q === visual.unit.pos.q && h.r === visual.unit.pos.r);
          if (inShape) this.queue.pushFlourish(new KnockupVFX(visual, this.layout.size));
        }
        break;
      }
      case "renektonDominus": {
        const renektonTargetPixels = shape.map(h => this.layout.hexToPixel(h));
        this.queue.pushFlourish(new RenektonFuryVFX(casterPos, renektonTargetPixels, this.layout.size * 2.2));
        break;
      }
      case "blitzcrankPunch": {
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size * 1.2, "#e8aa20"));
        const punchTargetVisual = [...this.visuals.values()].find(
          v => v.unit.team !== caster.team && v.unit.currentHp > 0 &&
               shape.some(h => h.q === v.unit.pos.q && h.r === v.unit.pos.r)
        );
        if (punchTargetVisual) {
          this.queue.pushFlourish(new KnockupVFX(punchTargetVisual, this.layout.size));
        }
        break;
      }
      case "yuumiHeal": {
        // Bond — sparkles arc from Yuumi to the attached ally, then a clock dial pulse with "−1 CD".
        const allyHex = shape[0];
        if (allyHex) {
          const allyVisual = [...this.visuals.values()].find(
            v => v.unit.team === caster.team && v.unit.currentHp > 0 &&
                 v.unit.pos.q === allyHex.q && v.unit.pos.r === allyHex.r
          );
          if (allyVisual) {
            this.queue.pushFlourish(new YuumiBondVFX(
              casterPos,
              { x: allyVisual.x, y: allyVisual.y },
              this.layout.size,
            ));
            // Subtle empower flash on the ally for body feedback.
            this.queue.pushFlourish(new HealFlashVFX(allyVisual));
          }
        }
        break;
      }
      case "neekoBounceQ": {
        if (shape.length === 0) break;
        const neekoEnemyTeam = caster.team === "player" ? "enemy" : "player";
        // positions chain: casterPos → target0 → target1 → ...
        const neekoPositions = [casterPos, ...shape.map(h => {
          const v = [...this.visuals.values()].find(
            vis => vis.unit.pos.q === h.q && vis.unit.pos.r === h.r && vis.unit.team === neekoEnemyTeam
          );
          return v ? { x: v.x, y: v.y } : this.layout.hexToPixel(h);
        })];
        this.pendingNeekoBounceHits = [];
        const neekoHits = this.pendingNeekoBounceHits;
        let neekoHitIdx = 0;
        this.queue.pushFlourish(new NeekoBounceVFX(neekoPositions, this.layout.size, () => {
          const h = neekoHits[neekoHitIdx++];
          if (h) {
            const healthBar = this.healthBars.get(h.unitId);
            if (healthBar) healthBar.unpin();
            this.queue.pushFlourish(new HitFlashVFX(h.visual));
            this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
            if (h.died) {
              this.pendingNeekoDeaths.delete(h.unitId);
              this.triggerDeathAnim(h.unitId);
            }
          }
        }));
        break;
      }
      case "nidaleeJavelin": {
        const nidaleeVisual = this.visuals.get(caster.id);
        this.pendingNidaleeSpearHit = null;
        this.pendingNidaleeBiteHit = null;
        this.blockingVfxCount++;

        const spearVfx = new NidaleeSpearVFX(casterPos, targetPos, this.layout.size, () => {
          // Spear landed — show damage popup
          const sh = this.pendingNidaleeSpearHit;
          if (sh) this.queue.pushFlourish(new DamagePopup(sh.x, sh.y - 18, sh.amount, sh.color));

          if (nidaleeVisual) {
            const startPx = { x: nidaleeVisual.x, y: nidaleeVisual.y };
            this.dashedUnits.add(caster.id);
            const pounceVfx = new NidaleePounceVFX(
              nidaleeVisual, startPx, targetPos, this.layout.size,
              () => {
                const bh = this.pendingNidaleeBiteHit;
                if (bh) this.queue.pushFlourish(new DamagePopup(bh.x, bh.y - 18, bh.amount, bh.color));
                this.queue.pushFlourish(new DamagePopup(targetPos.x, targetPos.y - 34, 0, "#ff8820", "POUNCE!"));
              },
            );
            pounceVfx.onEnd = () => {
              nidaleeVisual.x = startPx.x;
              nidaleeVisual.y = startPx.y;
              this.dashedUnits.delete(caster.id);
              this.blockingVfxCount--;
            };
            this.queue.pushFlourish(pounceVfx);
          } else {
            this.blockingVfxCount--;
          }
        });
        this.queue.pushFlourish(spearVfx);
        break;
      }
      default:
        this.queue.pushFlourish(new AoERingVFX(casterPos, this.layout.size, "#9b5bd6"));
        break;
    }
  }

  private onStatusApplied(unit: Unit, effect: StatusEffect): void {
    const visual = this.visuals.get(unit.id);
    if (!visual) return;
    const pos = { x: visual.x, y: visual.y };

    switch (effect.kind) {
      case "stun": {
        this.queue.pushFlourish(new HitFlashVFX(visual));
        this.queue.pushFlourish(new DamagePopup(pos.x, pos.y - 24, 0, "#ffd700", "STUNNED"));
        break;
      }
      case "stasis": {
        this.queue.pushFlourish(new AoERingVFX(pos, this.layout.size * 1.2, "#d4aa20"));
        this.queue.pushFlourish(new DamagePopup(pos.x, pos.y - 24, 0, "#d4aa20", "STASIS"));
        break;
      }
      case "shield": {
        this.queue.pushFlourish(
          new DamagePopup(pos.x, pos.y - 24, effect.amount, "#ffd700", "SHIELD"),
        );
        break;
      }
      case "damageReduction": {
        this.queue.pushFlourish(
          new DamagePopup(pos.x, pos.y - 24, 0, "#ffaa20", "ECLIPSE"),
        );
        break;
      }
      case "olafCcImmune": {
        this.queue.pushFlourish(new DamagePopup(pos.x, pos.y - 24, 0, "#ff5500", "IMMUNE!"));
        break;
      }
      case "alphaStrike": {
        this.queue.pushFlourish(new DamagePopup(pos.x, pos.y - 24, 0, "#80e8ff", "ALPHA!"));
        break;
      }
      default:
        break;
    }
  }

  private onDamage(
    target: Unit,
    amount: number,
    type: string,
    suppressPopup?: boolean,
    orbPass?: "out" | "back",
    alphaSlice?: boolean,
    jarvanPass?: boolean,
    ezrealPass?: boolean,
    tentacleHit?: boolean,
    caitlynHeadshot?: boolean,
    hookPass?: boolean,
    katarinaSlice?: boolean,
    requiemBeam?: boolean,
    luxBeam?: boolean,
    mundoLightning?: boolean,
    chogathRupture?: boolean,
    kaisaRocket?: boolean,
    sivirBounce?: boolean,
    dravenAxe?: boolean,
    jinxRocket?: boolean,
    nidaleeSpear?: boolean,
    nidaleeBite?: boolean,
    neekoBounce?: boolean,
    viDash?: boolean,
    azirDash?: boolean,
    nasusAxe?: boolean,
    jaxSlam?: boolean,
  ): void {
    const visual = this.visuals.get(target.id);
    if (!visual) return;
    if (amount <= 0) return;
    if (!neekoBounce && !jaxSlam && !kaisaRocket) this.queue.pushFlourish(new HitFlashVFX(visual));
    const color = type === "magical" ? "#c8a0ff" : "#ffd8b0";
    if (orbPass) {
      this.pendingOrbPopups.push({ x: visual.x, y: visual.y, amount, color, pass: orbPass });
    } else if (alphaSlice) {
      this.pendingAlphaHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (jarvanPass) {
      this.pendingJarvanHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (ezrealPass) {
      this.pendingEzrealHit = { x: visual.x, y: visual.y, amount, color };
    } else if (tentacleHit) {
      this.pendingTentacleHit = { x: visual.x, y: visual.y, amount, color };
    } else if (caitlynHeadshot) {
      this.pendingCaitlynHeadshotHit = { x: visual.x, y: visual.y, amount, color };
    } else if (hookPass) {
      this.pendingHookHit = { x: visual.x, y: visual.y, amount, color };
    } else if (katarinaSlice) {
      this.pendingKatarinaHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (requiemBeam) {
      this.pendingRequiemHits.set(target.id, { x: visual.x, y: visual.y, amount, color });
    } else if (luxBeam) {
      this.pendingLuxHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (mundoLightning) {
      if (this.mundoLightningCasterPos) {
        this.queue.pushFlourish(new LightningZapVFX(this.mundoLightningCasterPos, { x: visual.x, y: visual.y }));
      }
      this.queue.pushFlourish(new DamagePopup(visual.x, visual.y - 18, amount, color));
    } else if (chogathRupture) {
      this.queue.pushFlourish(new KnockupVFX(visual, this.layout.size));
      this.queue.pushFlourish(new DamagePopup(visual.x, visual.y - 18, amount, "#fca5a5"));
    } else if (kaisaRocket) {
      const healthBar = this.healthBars.get(target.id);
      if (healthBar) healthBar.pinAt(target.currentHp + amount);
      const died = target.currentHp <= 0;
      if (died) this.pendingKaisaDeaths.add(target.id);
      this.pendingKaisaHits.push({ x: visual.x, y: visual.y, amount, color: "#e0aaff", visual, unitId: target.id, died });
    } else if (sivirBounce) {
      this.pendingSivirHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (dravenAxe) {
      this.pendingDravenHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (jinxRocket) {
      this.pendingJinxHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (nidaleeSpear) {
      this.pendingNidaleeSpearHit = { x: visual.x, y: visual.y, amount, color };
    } else if (nidaleeBite) {
      this.pendingNidaleeBiteHit = { x: visual.x, y: visual.y, amount, color };
    } else if (neekoBounce) {
      const healthBar = this.healthBars.get(target.id);
      if (healthBar) healthBar.pinAt(target.currentHp + amount);
      const died = target.currentHp <= 0;
      if (died) this.pendingNeekoDeaths.add(target.id);
      this.pendingNeekoBounceHits.push({ x: visual.x, y: visual.y, amount, color: "#88ffee", visual, unitId: target.id, died });
    } else if (viDash) {
      this.pendingViDashHit = { x: visual.x, y: visual.y, amount, color };
    } else if (azirDash) {
      this.pendingAzirDashHits.push({ x: visual.x, y: visual.y, amount, color });
    } else if (nasusAxe) {
      this.pendingNasusAxeHit = { x: visual.x, y: visual.y, amount, color };
    } else if (jaxSlam) {
      this.pendingJaxSlamHits.push({ x: visual.x, y: visual.y, amount, color, visual });
    } else if (suppressPopup) {
      this.pendingSpinPopups.push({ x: visual.x, y: visual.y, amount, color });
    } else {
      this.queue.pushFlourish(new DamagePopup(visual.x, visual.y - 18, amount, color));
    }
  }

  private onHealed(unit: Unit, amount: number): void {
    const visual = this.visuals.get(unit.id);
    if (!visual || amount <= 0) return;
    this.queue.pushFlourish(new HealFlashVFX(visual));
    this.queue.pushFlourish(new DamagePopup(visual.x, visual.y - 18, amount, "#22c55e", `+${amount}`));
  }

  private onDodged(unit: Unit): void {
    const visual = this.visuals.get(unit.id);
    if (!visual) return;
    this.queue.pushFlourish(new DamagePopup(visual.x, visual.y - 24, 0, "#a855f7", "DODGE!"));
  }

  private onJaxSlam(unit: Unit): void {
    const visual = this.visuals.get(unit.id);
    const hits = this.pendingJaxSlamHits;
    this.pendingJaxSlamHits = [];

    const enemyR = unit.team === "player" ? 1 : 0;
    const slot0px = this.layout.hexToPixel({ q: 0, r: enemyR });
    const slot1px = this.layout.hexToPixel({ q: 1, r: enemyR });
    const slamPx = { x: (slot0px.x + slot1px.x) / 2, y: (slot0px.y + slot1px.y) / 2 };

    const fireImpact = () => {
      for (const h of hits) {
        this.queue.pushFlourish(new HitFlashVFX(h.visual));
        this.queue.pushFlourish(new DamagePopup(h.x, h.y - 18, h.amount, h.color));
      }
    };

    if (!visual) {
      fireImpact();
      return;
    }

    const startPx = { x: visual.x, y: visual.y };
    this.dashedUnits.add(unit.id);
    this.movementPauseMs = Math.max(this.movementPauseMs, 950);
    const vfx = new JaxSlamVFX(visual, startPx, slamPx, this.layout.size * 1.8, fireImpact);
    vfx.onEnd = () => {
      visual.x = startPx.x;
      visual.y = startPx.y;
      this.dashedUnits.delete(unit.id);
    };
    this.queue.pushFlourish(vfx);
  }

  private onDied(unit: Unit): void {
    this.movementPauseMs = Math.max(this.movementPauseMs, TIMING.deathFade + 80);
    if (this.pendingNeekoDeaths.has(unit.id)) return;
    if (this.pendingKaisaDeaths.has(unit.id)) return;
    this.triggerDeathAnim(unit.id);
  }

  private triggerDeathAnim(unitId: string): void {
    const visual = this.visuals.get(unitId);
    if (!visual) return;
    this.dashedUnits.add(unitId);
    const anim = new DeathAnim(visual);
    anim.onEnd = () => {
      visual.alpha = 0;
      visual.dead = true;
      this.dashedUnits.delete(unitId);
    };
    this.queue.pushFlourish(anim);
    const spin = this.activeSpinVfx.get(unitId);
    if (spin) {
      spin.cancel();
      this.activeSpinVfx.delete(unitId);
    }
  }

  private onSpawned(unit: Unit): void {
    // Register a new visual mid-combat (e.g. Tibbers summoned by Annie)
    if (!this.visuals.has(unit.id)) {
      this.registerUnit(unit);
    }
  }
}
